import { AppState, type AppStateStatus } from 'react-native';
import { SessionStorage } from './session-storage';
import { v4 as uuidv4 } from 'uuid';

export const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
export const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

let initialized = false;
let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
let warnedBeforeInitialize = false;

function isSessionExpired() {
  const lastActiveAt = SessionStorage.getLastActiveAt();
  if (lastActiveAt === undefined) {
    return true;
  }
  return Date.now() - lastActiveAt > SESSION_TIMEOUT_MS;
}

function rotateSession() {
  const id = uuidv4();
  SessionStorage.setSessionId(id);
  SessionStorage.setLastActiveAt(Date.now());
  return id;
}

/**
 * Returns the current session ID.
 * Creates a new session if none exists. Does NOT check expiry —
 * expiry is only evaluated on foreground resume and app startup.
 */
export function getCurrentSessionId() {
  if (__DEV__ && !initialized && !warnedBeforeInitialize) {
    warnedBeforeInitialize = true;
    console.warn(
      'getCurrentSessionId() called before initializeSessionManager(); session expiry has not been evaluated yet.',
    );
  }

  const existing = SessionStorage.getSessionId();
  if (!existing) {
    return rotateSession();
  }
  return existing;
}

/** Force-rotates to a new session. Called on logout / Analytics.reset(). */
export function resetSession() {
  rotateSession();
}

// Periodically persists lastActiveAt while the app is foregrounded so that
// a crash or force-quit leaves the timestamp at most HEARTBEAT_INTERVAL_MS
// stale. Without this, a long foreground session followed by a kill would
// look expired on next launch even if the user was only away briefly.
function startHeartbeat() {
  stopHeartbeat();
  heartbeatTimer = setInterval(() => {
    SessionStorage.setLastActiveAt(Date.now());
  }, HEARTBEAT_INTERVAL_MS);
}

function stopHeartbeat() {
  if (heartbeatTimer !== null) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
}

/** Sets up AppState listener for background/foreground detection. Call once at startup. */
export function initializeSessionManager() {
  if (initialized) {
    return;
  }
  initialized = true;

  // On startup, rotate if the previous session has expired (e.g. app was killed
  // and relaunched after SESSION_TIMEOUT_MS). Otherwise ensure a session exists.
  if (isSessionExpired()) {
    rotateSession();
  } else {
    getCurrentSessionId();
    SessionStorage.setLastActiveAt(Date.now());
  }

  startHeartbeat();
  AppState.addEventListener('change', handleAppStateChange);
}

function handleAppStateChange(nextState: AppStateStatus) {
  if (nextState === 'active') {
    if (isSessionExpired()) {
      rotateSession();
    } else {
      SessionStorage.setLastActiveAt(Date.now());
    }
    startHeartbeat();
  } else {
    // 'background' or 'inactive' (iOS) — persist timestamp and stop heartbeat.
    // On web, AppState change is driven by document visibility, so switching to
    // another tab starts the same inactivity timeout as backgrounding the app.
    SessionStorage.setLastActiveAt(Date.now());
    stopHeartbeat();
  }
}
